import React, { useEffect, useState } from 'react';
import './SmartInput.css';

// Custom hook to rotate placeholder text
const useRotatingPlaceholder = (options, interval = 2000) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setIndex(prev => (prev + 1) % options.length);
    }, interval);
    return () => clearInterval(timer);
  }, [options, interval]);
  return options[index]?.label || options[index] || '';
};

const SmartInput = ({
  id,
  label,
  value,
  onChange,
  options = null,
  isTextarea = false,
  type = 'text', // can be 'text', 'checkbox'
}) => {
  const rotatingPlaceholder = useRotatingPlaceholder(options || []);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputText, setInputText] = useState(value || '');

  // Update internal inputText when external value changes
  useEffect(() => {
    setInputText(value || '');
  }, [value]);

  useEffect(() => {
    if (options) {
      setFilteredOptions(
        options.filter(opt => {
          const label = typeof opt === 'string' ? opt : opt.label;
          return label.toLowerCase().includes((inputText || '').toLowerCase());
        })
      );
    }
  }, [inputText, options]);

  const handleSelect = (option) => {
    const selectedValue = typeof option === 'string' ? option : option.value;
    setInputText(selectedValue);
    onChange({ target: { value: selectedValue } });
    setShowDropdown(false);
  };

  // Checkbox type input
  if (type === 'checkbox') {
    return (
      <div className="smart-input-container">
        <label htmlFor={id} className="input-label checkbox-label">
          <input
            type="checkbox"
            id={id}
            checked={value === 'Yes' || value === true}
            onChange={(e) =>
              onChange({ target: { value: e.target.checked ? 'Yes' : 'No' } })
            }
          />
          {label}
        </label>
      </div>
    );
  }

  return (
    <div className="smart-input-container">
      <label htmlFor={id} className="input-label">{label}</label>

      {isTextarea ? (
        <textarea
          id={id}
          placeholder={rotatingPlaceholder}
          value={value}
          onChange={onChange}
          className="input-field textarea-field"
        />
      ) : options ? (
        <>
          <input
            id={id}
            placeholder={rotatingPlaceholder}
            value={inputText}
            onChange={(e) => {
              const val = e.target.value;
              setInputText(val);
              onChange({ target: { value: val } }); // Sync custom input
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 100)}
            className="input-field"
          />
          {showDropdown && filteredOptions.length > 0 && (
            <div className="dropdown-list">
              {filteredOptions.map((opt, i) => (
                <div
                  key={i}
                  className="dropdown-item"
                  onMouseDown={() => handleSelect(opt)}
                >
                  {typeof opt === 'string' ? opt : opt.label}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <input
          id={id}
          placeholder={label}
          value={value}
          onChange={onChange}
          className="input-field"
        />
      )}
    </div>
  );
};

export default SmartInput;
